import { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { DateTime } from 'luxon';
import { dataHooks } from '@sunrun/recent-performance-charts';
import config from '../config';
import { getAuthToken } from '../providers/authTokenProvider';
import {
  fetchBatteryDataIfNeeded,
  fetchNotificationsIfNeeded,
  fetchSystemOverviewIfNeeded,
  getBatteries,
  getBatteryData,
  getHasBattery,
  getHasNonCommAlert,
  getLastUpdatedLoading,
  getNotifications,
  getPGThreshold,
  getSystemLoading,
  getSystemOverview,
} from '../redux/modules/system';
import { useProspectId, useContract } from './useUser';

const { useMinutePerformance } = dataHooks;

export const useSystemOverview = () => {
  const system = useSelector(getSystemOverview);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(fetchSystemOverviewIfNeeded());
  }, []);

  return system;
};

export const useLastUpdated = () => {
  const authToken = getAuthToken();
  const prospectId = useProspectId();

  const { timezone } = useSystemOverview();
  const endDate = DateTime.fromISO(DateTime.local(), { zone: timezone }).endOf('day').toISO();
  const startDate = DateTime.fromISO(endDate, { zone: timezone }).startOf('day').minus({ days: 7 }).toISO();
  const everyFifteenMinuteProductionItems = useMinutePerformance(prospectId, startDate, endDate, config.PERFORMANCE_BASE_URL, authToken);

  if (!everyFifteenMinuteProductionItems || everyFifteenMinuteProductionItems.length === 0) {
    return null;
  }

  return everyFifteenMinuteProductionItems.reduce((first, second) => (first.timestamp > second.timestamp ? first : second)).timestamp;
};

export const useLastUpdatedLoading = () => {
  const loading = useSelector(getLastUpdatedLoading);

  return loading;
};

export const useBatteryData = refreshBatteryData => {
  const system = useSelector(getSystemOverview);
  const battery = useSelector(getBatteryData);
  const contract = useContract();
  const dispatch = useDispatch();

  useEffect(() => {
    // if the system has batteries
    if (contract && system.batteries && system.batteries.length > 0) {
      dispatch(fetchBatteryDataIfNeeded(contract.id, refreshBatteryData));
    }
  }, [contract, system]);

  return battery;
};

export const useHasBattery = () => {
  const hasBattery = useSelector(getHasBattery);
  return hasBattery;
};

export const usePGThreshold = () => {
  const pgThreshold = useSelector(getPGThreshold);
  return pgThreshold;
};

export const useBatteryVendors = () => {
  const batteries = useSelector(getBatteries);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(fetchSystemOverviewIfNeeded());
  }, []);

  if (!batteries) {
    return [];
  }

  return batteries?.map(battery => battery.vendorName?.toLowerCase());
};

export const useSystemLoading = () => {
  const loading = useSelector(getSystemLoading);

  return loading;
};

export const useNotifications = () => {
  const currentNotifications = useSelector(getNotifications);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(fetchNotificationsIfNeeded());
  }, []);

  return currentNotifications;
};

export const useHasNonCommAlert = () => {
  const nonCommAlert = useSelector(getHasNonCommAlert);
  return nonCommAlert;
};
